import React from 'react';
import {
  Box,
  FormControlLabel,
  FormControlLabelText,
  HStack,
  VStack,
  ScrollView,
  Button,
  ButtonText,
  ButtonSpinner,
  ButtonIcon,
} from '@gluestack-ui/themed';
import { Mail, Key } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useFormContext, Controller } from 'react-hook-form';
import FormField from '../../../../common/components/forms/FormField';
import SettingsGroupCard from '../../../../common/components/forms/SettingsGroupCard';
import ObjectIdField from '../../../common/ObjectIdField';
import { useDialog } from '../../../../common/context/DialogContext';
import { useApiErrorReporter } from '../../../../common/utils/apiErrorReporter';
import { useTriggerPasswordReset } from '../../../../common/api/endpoints/users/users';
import { ReferenceSelectField } from '../../../common/references';
import AdminSetPasswordModal from './AdminSetPasswordModal';

const COHORT_REFERENCE_FILTER = { isDeprecated: false, state: ['active', 'stale'] };
const COURSE_REFERENCE_FILTER = { isDeprecated: false, state: 'active' };

/**
 * UserDetailsForm - Core form for viewing and editing user personal information,
 * account status, and role-specific configurations (like student trials).
 *
 * This component is intended to be used within a `FormProvider` from `react-hook-form`.
 * It handles:
 * - Syncing username with email for new users.
 * - Enforcing trial eligibility (students only).
 * - Providing default values for trial banner configuration.
 * - Triggering password resets and opening the manual password modal.
 * - Validating and formatting date strings for trial start dates.
 *
 * @returns {React.ReactElement} The rendered form.
 */
const UserDetailsForm = () => {
  const { watch, setValue, getValues } = useFormContext();
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const { t } = useTranslation();
  const triggerResetMutation = useTriggerPasswordReset();
  const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = React.useState(false);

  // Add email-to-username syncing for new users
  const id = watch('id');
  const email = watch('email');
  React.useEffect(() => {
    if (!id && email) {
      setValue('username', email);
    }
  }, [id, email, setValue]);

  const trial = watch('trial') || {};

  // Set trial defaults when enabled
  const isTrial = watch('trial.isTrial');
  const role = watch('role') || 'student';

  // Only allow trials for student users
  React.useEffect(() => {
    if (role !== 'student') {
      if (isTrial) setValue('trial.isTrial', false);
      // Clear course enrollment if role is not student (onboarding only)
      if (!id) setValue('course.id', '');
    }
  }, [role, isTrial, setValue, id]);

  React.useEffect(() => {
    if (isTrial && role === 'student') {
      const values = getValues('trial') || {};
      if (!values.message) setValue('trial.message', 'This is a trial you cannot save your work. You have {DAYS_LEFT} days left.');
      if (!values.startdate) setValue('trial.startdate', new Date().toISOString().split('T')[0]);
      if (!values.durationDays) setValue('trial.durationDays', '30');
      if (!values.ctaLabel) setValue('trial.ctaLabel', 'Upgrade Now');
      if (!values.ctaLink) setValue('trial.ctaLink', 'https://www.steinbeis-next.de/apply-now/');
    }
  }, [isTrial, role, setValue, getValues]);

  // Format incoming date on initial load and when trial.startdate changes
  const trialStartDate = watch('trial.startdate');
  React.useEffect(() => {
    if (trialStartDate && trialStartDate.includes('T')) {
      const date = new Date(trialStartDate);
      if (!Number.isNaN(date.getTime())) {
        setValue('trial.startdate', date.toISOString().split('T')[0], { shouldValidate: true });
      }
    }
  }, [trialStartDate, setValue]);

  const validateDate = (value) => {
    if (!value) return 'Trial start date is required';

    // First check if it matches basic YYYY-MM-DD format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return 'Invalid date format. Expected YYYY-MM-DD';
    }

    // Try to parse the date
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    // Check if date is valid and matches input
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return 'Invalid date';
    }

    return true; // Valid date
  };

  return (
    <ScrollView flex={1} bg="$backgroundLight0">
      <VStack space="xl" py="$6" testID="user-details-form">
        <VStack space="xl" width="100%">
          <VStack space="xl" flex={1}>
            <HStack space="lg" flexWrap="wrap" alignItems="stretch">
              <SettingsGroupCard title="Personal Information" flex={1} minWidth={480}>
                <VStack space="lg">
                  <HStack space="lg" flexWrap="wrap">
                    <FormField
                      name="firstname"
                      label="Firstname"
                      type="text"
                      fieldWidth="220px"
                      rules={{ required: 'Firstname is required' }}
                    />
                    <FormField
                      name="lastname"
                      label="Lastname"
                      type="text"
                      fieldWidth="220px"
                      rules={{ required: 'Lastname is required' }}
                    />
                  </HStack>
                  <HStack space="lg" alignItems="flex-end" flexWrap="wrap">
                    <FormField
                      name="email"
                      label="Email"
                      type="text"
                      fieldWidth="220px"
                      rules={{
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      }}
                    />
                    {watch('role') === 'lecturer' && (
                      <FormField
                        name="title"
                        label="Academic Title"
                        type="text"
                        placeholder="e.g. Dr."
                        fieldWidth="180px"
                      />
                    )}
                    {watch('role') === 'student' && (
                      <Box width={220}>
                        <FormControlLabel mb="$1">
                          <FormControlLabelText size="xs">Assigned Cohort</FormControlLabelText>
                        </FormControlLabel>
                        <Controller
                          name="cohort.id"
                          defaultValue=""
                          render={({ field: { value: cohortIdValue, onChange: onCohortIdChange } }) => {
                            const cohortTitleValue = watch('cohort.title');
                            const cohortValue = cohortIdValue
                              ? { id: cohortIdValue, title: cohortTitleValue ?? null }
                              : null;
                            return (
                              <ReferenceSelectField
                                testID="user-cohort-reference"
                                objectName="cohorts"
                                fixedFilter={COHORT_REFERENCE_FILTER}
                                allowClear
                                value={cohortValue}
                                onChange={(next) => {
                                  onCohortIdChange(next?.id ?? '');
                                  setValue('cohort.title', next?.title ?? '', { shouldDirty: true });
                                }}
                              />
                            );
                          }}
                        />
                      </Box>
                    )}
                  </HStack>
                </VStack>
              </SettingsGroupCard>

              {!id && (
                <SettingsGroupCard title="Onboarding" width={340}>
                  <VStack space="lg">
                    {role === 'student' && (
                      <Box width="100%">
                        <FormControlLabel mb="$1">
                          <FormControlLabelText size="xs">Enroll in Course</FormControlLabelText>
                        </FormControlLabel>
                        <Controller
                          name="course.id"
                          defaultValue=""
                          render={({ field: { value: courseIdValue, onChange: onCourseIdChange } }) => {
                            const courseTitleValue = watch('course.title');
                            const courseValue = courseIdValue
                              ? { id: courseIdValue, title: courseTitleValue ?? null }
                              : null;
                            return (
                              <ReferenceSelectField
                                testID="user-course-reference"
                                objectName="courses"
                                fixedFilter={COURSE_REFERENCE_FILTER}
                                allowClear
                                value={courseValue}
                                onChange={(next) => {
                                  onCourseIdChange(next?.id ?? '');
                                  setValue('course.title', next?.title ?? '', { shouldDirty: true });
                                }}
                              />
                            );
                          }}
                        />
                      </Box>
                    )}
                    <VStack space="md" mt="$2">
                      <FormField
                        name="sendWelcomeEmail"
                        label="Send welcome email with credentials"
                        type="checkbox"
                        defaultValue
                      />
                      {watch('sendWelcomeEmail') && (
                        <FormField
                          name="includePassword"
                          label="Include login credentials"
                          type="checkbox"
                          defaultValue
                        />
                      )}
                    </VStack>
                  </VStack>
                </SettingsGroupCard>
              )}
            </HStack>

            <HStack space="lg" flexWrap="wrap" alignItems="stretch">
              <SettingsGroupCard title="Account Access & Status" flex={1} minWidth={480}>
                <VStack space="lg">
                  <HStack space="lg" flexWrap="wrap">
                    <FormField
                      name="username"
                      label="Username"
                      readonly
                      type="text"
                      fieldWidth="280px"
                      rules={{ required: 'Username is required' }}
                    />
                    <FormField
                      name="role"
                      label="Role"
                      type="select"
                      fieldWidth="280px"
                      defaultValue="student"
                      choices={[
                        { value: 'student', label: 'Student' },
                        { value: 'lecturer', label: 'Lecturer' },
                        { value: 'admin', label: 'Administrator' },
                      ]}
                      rules={{ required: 'Role is required' }}
                    />
                    {watch('role') === 'student' && (
                      <FormField
                        name="externalStudentId"
                        label={t('admin.userDetails.externalStudentId')}
                        type="text"
                        placeholder={t('admin.userDetails.externalStudentIdPlaceholder')}
                        fieldWidth="260px"
                      />
                    )}
                  </HStack>
                  <HStack space="lg" alignItems="flex-end" flexWrap="wrap">
                    {watch('role') === 'student' && (
                      <FormField
                        name="state"
                        label="Lifecycle State"
                        type="select"
                        fieldWidth="280px"
                        defaultValue="active"
                        choices={[
                          { value: 'active', label: 'Active' },
                          { value: 'stale', label: 'Stale' },
                          { value: 'finished', label: 'Finished' },
                          { value: 'inactive', label: 'Inactive' },
                        ]}
                        rules={{ required: 'Lifecycle state is required' }}
                      />
                    )}
                    {watch('id') && (
                      <HStack space="md" alignItems="flex-end">
                        <Button
                          variant="outline"
                          action="secondary"
                          size="sm"
                          onPress={async () => {
                            await triggerResetMutation.mutateAsync(
                              { userId: watch('id') },
                              {
                                onSuccess: () => {
                                  showToastMessage('success', t('admin.userDetails.resetTriggered'));
                                },
                                onError: (error) => {
                                  reportApiError(error, t('admin.userDetails.triggerReset'), 'toast', {
                                    expectedErrorCodes: ['AUTH_USER_INACTIVE'],
                                  });
                                },
                              },
                            );
                          }}
                          isDisabled={triggerResetMutation.isPending}
                          mb="$1"
                        >
                          {triggerResetMutation.isPending ? (
                            <ButtonSpinner mr="$2" />
                          ) : (
                            <ButtonIcon as={Mail} size="xs" mr="$2" />
                          )}
                          <ButtonText>{t('admin.userDetails.triggerReset')}</ButtonText>
                        </Button>
                        <Button
                          variant="outline"
                          action="secondary"
                          size="sm"
                          onPress={() => setIsSetPasswordModalOpen(true)}
                          mb="$1"
                        >
                          <ButtonIcon as={Key} size="xs" mr="$2" />
                          <ButtonText>{t('admin.userDetails.setPassword')}</ButtonText>
                        </Button>
                      </HStack>
                    )}
                  </HStack>
                  {watch('role') === 'student' && (
                    <FormField
                      name="trial.isTrial"
                      fieldSize="sm"
                      labelSize="sm"
                      label="Limited Trial"
                      type="checkbox"
                    />
                  )}
                </VStack>
              </SettingsGroupCard>

              {/* Sidebar / Trial Settings */}
              {watch('role') === 'student' && trial.isTrial && (
                <SettingsGroupCard title="Trial Configuration" width={340}>
                  <VStack space="lg">
                    <FormField
                      name="trial.message"
                      label="Banner Message"
                      type="text"
                      placeholder="e.g. 'This is a trial you cannot save your work. You have {DAYS_LEFT} days left.'"
                      fieldWidth="100%"
                    />
                    <HStack space="md">
                      <FormField
                        name="trial.startdate"
                        label="Start Date"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        fieldWidth="140px"
                        rules={{
                          required: 'Trial start date is required',
                          validate: validateDate,
                        }}
                      />
                      <FormField
                        name="trial.durationDays"
                        label="Duration (days)"
                        type="text"
                        placeholder="30"
                        fieldWidth="140px"
                        rules={{
                          required: 'Trial start date is required',
                          min: { value: 1, message: 'Min 1' },
                          max: { value: 365, message: 'Max 365' },
                          pattern: { value: /^[1-9]\d*$/, message: 'Positive number only' },
                        }}
                      />
                    </HStack>
                    <FormField
                      name="trial.ctaLabel"
                      label="CTA Button Label"
                      type="text"
                      placeholder="e.g. 'Upgrade Now'"
                      fieldWidth="100%"
                    />
                    <FormField
                      name="trial.ctaLink"
                      label="CTA Button Link"
                      type="text"
                      placeholder="https://..."
                      fieldWidth="100%"
                    />
                  </VStack>
                </SettingsGroupCard>
              )}
            </HStack>

            <ObjectIdField />
          </VStack>
        </VStack>
      </VStack>
      <AdminSetPasswordModal
        isOpen={isSetPasswordModalOpen}
        onClose={() => setIsSetPasswordModalOpen(false)}
        userId={watch('id')}
      />
    </ScrollView>
  );
};

export default UserDetailsForm;
